import React, { useState } from "react";
import { Edit3, Eye, Columns, Bold, Italic, Heading, List, Quote, Code as CodeIcon } from "lucide-react";
import { renderMarkdown } from "@/lib/markdown";

const MODES = [
  { key: "edit", icon: Edit3, label: "Bearbeiten" },
  { key: "split", icon: Columns, label: "Geteilt" },
  { key: "preview", icon: Eye, label: "Vorschau" },
];

const TOOLBAR = [
  { icon: Heading, label: "H2", insert: (s) => `## ${s || "Überschrift"}`, block: true },
  { icon: Bold, label: "B", insert: (s) => `**${s || "fett"}**` },
  { icon: Italic, label: "I", insert: (s) => `*${s || "kursiv"}*` },
  { icon: List, label: "•", insert: (s) => `- ${s || "Listenpunkt"}`, block: true },
  { icon: Quote, label: '"', insert: (s) => `> ${s || "Zitat"}`, block: true },
  { icon: CodeIcon, label: "</>", insert: (s) => `\`${s || "code"}\`` },
];

export const MarkdownEditor = ({ value, onChange, rows = 14, testId, placeholder }) => {
  const [mode, setMode] = useState("split");
  const textareaRef = React.useRef(null);

  const applyToolbar = (item) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.substring(start, end);
    const replacement = item.insert(selected);
    const prefix = item.block && start > 0 && value[start - 1] !== "\n" ? "\n" : "";
    const newValue = value.substring(0, start) + prefix + replacement + value.substring(end);
    onChange(newValue);
    setTimeout(() => {
      ta.focus();
      const pos = start + prefix.length + replacement.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  return (
    <div className="border border-line bg-white" data-testid={`md-editor-${testId || "default"}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line bg-soft px-3 py-2">
        <div className="flex items-center gap-1">
          {TOOLBAR.map((it) => (
            <button
              key={it.label}
              type="button"
              onClick={() => applyToolbar(it)}
              title={it.label}
              data-testid={`md-toolbar-${it.label}`}
              className="w-8 h-8 flex items-center justify-center border border-transparent text-muted hover:border-line hover:bg-white hover:text-primary transition-colors text-sm font-mono"
            >
              <it.icon size={14} />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 border border-line bg-white">
          {MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setMode(m.key)}
              data-testid={`md-mode-${m.key}`}
              title={m.label}
              className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1.5 transition-colors ${
                mode === m.key ? "bg-primary text-white" : "text-muted hover:text-primary"
              }`}
            >
              <m.icon size={12} /> {m.label}
            </button>
          ))}
        </div>
      </div>

      <div className={`grid ${mode === "split" ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"} divide-x divide-line`}>
        {(mode === "edit" || mode === "split") && (
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={rows}
            placeholder={placeholder || "## Überschrift\n\nText mit **fett** und *kursiv*.\n\n- Listenpunkt 1\n- Listenpunkt 2\n\n> Ein Zitat."}
            data-testid={`md-textarea-${testId || "default"}`}
            className="w-full bg-white px-4 py-4 outline-none focus:bg-soft transition-colors resize-y font-mono text-sm leading-relaxed text-ink placeholder:text-muted"
          />
        )}
        {(mode === "preview" || mode === "split") && (
          <div
            data-testid={`md-preview-${testId || "default"}`}
            className="bg-white px-5 py-4 overflow-auto"
            style={{ minHeight: `${rows * 1.6}rem`, maxHeight: "70vh" }}
          >
            {value ? (
              <div
                className="prose-itsp prose-sm"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(value) }}
              />
            ) : (
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted">Noch kein Inhalt – Vorschau erscheint hier.</p>
            )}
          </div>
        )}
      </div>

      <div className="border-t border-line bg-soft px-3 py-1.5 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-muted">
        <span>Markdown unterstützt: # ## ###  **fett**  *kursiv*  - liste  &gt; zitat  `code`</span>
        <span>{value.length} Zeichen · {value.trim() ? value.trim().split(/\s+/).length : 0} Wörter</span>
      </div>
    </div>
  );
};
